import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, CircleUserRound, Phone, Sparkles } from "lucide-react";
import api from "@/lib/api";

const WORDS = [
  { word: "Business", color: "#67E8F9", desc: "On structure votre activité comme une vraie machine." },
  { word: "Boost",    color: "#FFDCA8", desc: "On démultiplie votre développement commercial." },
  { word: "Brand",    color: "#A7F3D0", desc: "On bâtit une marque qui se distingue vraiment." },
  { word: "Build",    color: "#67E8F9", desc: "On crée vos outils métier sur-mesure." },
  { word: "Besson",   color: "#06B6D4", desc: "Et au bout de la ligne, on reste à vos côtés." },
];

// Fallback (demo) — équipe en bureau, free license (mixkit)
const FALLBACK_MP4 = "https://assets.mixkit.co/videos/4818/4818-720.mp4";
const FALLBACK_POSTER = "https://images.unsplash.com/photo-1758518729685-f88df7890776?crop=entropy&cs=srgb&fm=jpg&w=1920&q=70";

function youtubeId(url) {
  if (!url || typeof url !== "string") return null;
  const m = url.match(/(?:youtube\.com\/(?:.*v=|embed\/|shorts\/)|youtu\.be\/)([\w-]{11})/);
  return m ? m[1] : null;
}

export default function HomeBHero() {
  const [idx, setIdx] = useState(0);
  const [heroUrl, setHeroUrl] = useState("");

  useEffect(() => {
    const t = setInterval(() => setIdx((i) => (i + 1) % WORDS.length), 2400);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    api.get("/settings/public").then((r) => setHeroUrl(r.data?.hero_video_url || "")).catch(() => {});
  }, []);

  const cur = WORDS[idx];
  const ytId = youtubeId(heroUrl);

  return (
    <section
      className="relative isolate overflow-hidden bg-gradient-to-br from-[#0A1128] via-[#14213d] to-[#0A1128] text-white pt-32 sm:pt-44 pb-24 sm:pb-32 min-h-[88vh] flex items-center"
      data-testid="home-b-hero"
    >
      {/* Background video — YouTube iframe if admin URL set, else mixkit MP4 fallback */}
      <div className="absolute inset-0 -z-10 overflow-hidden">
        {ytId ? (
          <iframe
            data-testid="hero-bg-youtube"
            title="Hero background"
            // Cover the section with a video that loops and is muted (YouTube requires mute for autoplay)
            // playlist=ytId is required for the loop param to work on a single video
            src={`https://www.youtube.com/embed/${ytId}?autoplay=1&mute=1&loop=1&playlist=${ytId}&controls=0&showinfo=0&modestbranding=1&rel=0&iv_load_policy=3&disablekb=1&playsinline=1`}
            allow="autoplay; encrypted-media; picture-in-picture"
            allowFullScreen={false}
            // Always cover full section: keep 16:9 and ensure both dims are ≥ viewport
            // 177.78vh = 100vh * 16/9, 56.25vw = 100vw * 9/16
            className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[max(100vw,177.78vh)] h-[max(56.25vw,100vh)] pointer-events-none"
          />
        ) : (
          <video
            data-testid="hero-bg-video"
            autoPlay
            muted
            loop
            playsInline
            poster={FALLBACK_POSTER}
            className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 min-w-full min-h-full w-auto h-auto max-w-none object-cover"
            style={{ width: "max(100vw, 177.78vh)", height: "max(56.25vw, 100vh)" }}
          >
            <source src={FALLBACK_MP4} type="video/mp4" />
          </video>
        )}
        {/* Dark overlay so cyan text & B remain legible */}
        <div className="absolute inset-0 bg-gradient-to-br from-[#0A1128]/85 via-[#0A1128]/75 to-[#0A1128]/85" />
        <div className="absolute inset-0 noise opacity-20" />
      </div>

      {/* Coloured background glow following the rotating word */}
      <div
        className="absolute -top-40 -right-40 w-[560px] h-[560px] rounded-full opacity-25 transition-colors duration-700 pointer-events-none"
        style={{ background: cur.color, filter: "blur(160px)" }}
      />
      <div
        className="absolute -bottom-32 -left-32 w-[420px] h-[420px] rounded-full opacity-15 pointer-events-none"
        style={{ background: "#06B6D4", filter: "blur(120px)" }}
      />

      <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        {/* Animated B + rotating word — centered, single focus */}
        <div className="mt-6 flex items-center justify-center gap-4 sm:gap-8 flex-wrap">
          <div className="relative shrink-0">
            <span className="absolute inset-0 -m-2 rounded-full blur-3xl transition-colors duration-700 opacity-60" style={{ background: cur.color }} />
            <div
              className="relative display font-black text-[9rem] sm:text-[14rem] leading-none select-none b-letter-wobble"
              style={{ color: cur.color, textShadow: "0 8px 60px rgba(103,232,249,0.35)" }}
              aria-hidden="true"
            >B</div>
          </div>

          <div className="flex flex-col items-start text-left">
            <span className="display font-extrabold text-white/90 text-3xl sm:text-[2.6rem] leading-[1.02] italic">comme</span>
            <span
              key={cur.word}
              data-testid="b-word"
              className="display font-black text-5xl sm:text-7xl leading-[1.02] b-word-in -mt-1 sm:-mt-2"
              style={{ color: cur.color }}
            >{cur.word}</span>
          </div>
        </div>

        <h1 className="display mt-10 text-3xl sm:text-5xl font-extrabold leading-[1.05] max-w-3xl mx-auto">
          Les copilotes de votre <span className="text-[#67E8F9]">activité.</span>
        </h1>

        <p
          key={cur.desc}
          className="mt-5 text-lg sm:text-xl text-white/80 max-w-2xl mx-auto leading-relaxed b-desc-in"
        >{cur.desc}</p>

        {/* CTAs */}
        <div className="mt-10 flex flex-wrap items-center justify-center gap-3">
          <a
            href="#decouverte"
            data-testid="hero-rdv-decouverte"
            className="group inline-flex items-center gap-2 px-7 py-4 rounded-full bg-[#06B6D4] text-[#0A1128] hover:bg-[#67E8F9] font-extrabold text-[15px] shadow-2xl transition-all hover:scale-[1.02]"
          >
            <Phone size={18} />
            Prendre RDV — 1<sup className="text-[10px]">er</sup> gratuit
            <ArrowRight size={16} className="group-hover:translate-x-0.5 transition-transform" />
          </a>
          <Link
            to="/portail/connexion"
            data-testid="hero-create-espace"
            className="inline-flex items-center gap-2 px-7 py-4 rounded-full border border-white/30 bg-white/5 hover:bg-white/10 text-white font-bold text-[15px] backdrop-blur transition-colors"
          >
            <CircleUserRound size={18} />
            Créer mon Espace Entreprise
          </Link>
        </div>

        <div className="mt-5 text-xs text-white/55 flex items-center justify-center gap-2">
          <Sparkles size={12} className="text-[#67E8F9]" />
          1 RDV découverte gratuit par entreprise · sans engagement
        </div>
      </div>
    </section>
  );
}
